// =============================================================================
// ui/tabs/Patterns.tsx — Setups and Exit Models.
// -----------------------------------------------------------------------------
// Transcribed from Josh's screenshots. Two sub-tabs: `Setups (11)` and
// `Exit Models (6)`.
//
// ── 🚨 TWO POPULATIONS, DELIBERATELY NOT SHARED (D77) ──
//
// The two sub-tabs reach their trades through two different beta functions, and
// as it happens the two rules coincide today. They are still written out twice,
// four lines apart, because D77 exists precisely because a shared population
// looked obviously right and was wrong on three surfaces in a row. If beta's
// `patternStats` and `exitModelStats` ever diverge, editing one memo here must not
// silently move the other tab's numbers.
//
// Each is pinned by a test that names its own rule.
//
// ── SCOPE: WHOLE BOOK, NOT THE DATE FILTER ──
//
// Every figure here is a claim about the user's whole history — "this exit model
// is +$1,085 over 19 trades", "this setup is 27 trades at 59%". Read UNBOUNDED,
// like the analytics sub-tabs; the global date range is deliberately ignored.
//
// ⚠ LAST 30D is the one exception, and it is not the date filter — it is a fixed
// 30-day window computed off the range anchor, matching beta's own `patternStats`.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { useTabMemory } from '../state/tabMemory';
import { useExitModels } from '../state/exitModels';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import { useRange } from '../state/range';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import { tradeValue, type PnlUnit } from '../../selectors/pnlUnit';
import { useSupabase } from '../state/supabase';
import { useTradeRows } from '../state/useTradeRows';
import { newTradeId } from '../../io/sync/assembleImported';
import { patternLifecycle, type PatternSummary } from '../../io/sync/patterns';
import { savePatternLifecycle } from '../../io/sync/savePattern';
import {
  exitModelBoard, sortExitModels, untaggedCount,
  type ExitModelBoardRow, type ExitModelSortKey, type ExitModelTrade,
} from '../../selectors/exitModelBoard';
import {
  patternBoard, patternCategories, patternGrid, sortPatterns,
  type PatternBoardRow, type PatternBoardTrade, type PatternSortKey,
} from '../../selectors/patternBoard';
import { promoteToProduction } from '../../selectors/patternVersions';
import { addDays } from '../../selectors/range';
import { effectiveTradeDate } from '../../selectors/population';
import type { ExitModelSummary } from '../../io/sync/exitModels';
import { ExitModelEditor } from './ExitModelEditor';
import { PatternEditor } from './PatternEditor';
import { pct, threeWay, unitCompact } from '../format';

const UNBOUNDED = { startDate: null, endDate: null };

type SubTab = 'setups' | 'exits';

const SORTS: readonly { key: ExitModelSortKey; label: string }[] = [
  { key: 'sample', label: 'Sample size' },
  { key: 'totalPnl', label: 'Net P&L' },
  { key: 'expectancy', label: 'Expectancy' },
  { key: 'winRate', label: 'Win rate' },
  { key: 'name', label: 'Name' },
];

/** Beta's five, in beta's order — "Sort: Sample size" is the default. */
const PAT_SORTS: readonly { key: PatternSortKey; label: string }[] = [
  { key: 'sample', label: 'Sample size' },
  { key: 'winRate', label: 'Win rate' },
  { key: 'expectancy', label: 'Expectancy' },
  { key: 'totalPnl', label: 'Total P&L' },
  { key: 'name', label: 'Name' },
];

export function PatternsTab() {
  const { sb, userId } = useSupabase();
  const { prefs } = usePreferences();
  const { patterns, reload: reloadPatterns } = usePatterns();
  const { exitModels, phase: emPhase, error: emError, reload } = useExitModels();
  const { today } = useRange();
  const { contracts } = useContracts();
  const { unit } = usePnlUnit();
  const mode = prefs?.dayBoundaryMode ?? null;

  const [sub, setSub] = useTabMemory<SubTab>('patterns:sub', 'setups');
  const [sort, setSort] = useState<ExitModelSortKey>('sample');
  const [patSort, setPatSort] = useState<PatternSortKey>('sample');
  // `undefined` = closed · `null` = New · a model = Edit.
  const [editing, setEditing] = useState<ExitModelSummary | null | undefined>(undefined);
  const [editingPattern, setEditingPattern] =
    useState<PatternSummary | null | undefined>(undefined);
  const [patError, setPatError] = useState<string | null>(null);

  const { phase, rows, error } = useTradeRows(UNBOUNDED, mode);

  /**
   * ── 🚨 THE POPULATION IS `annotated !== false`, AND NOTHING ELSE ──
   *
   * Transcribed from `exitModelStats`, which is one line:
   *
   *   state.trades.filter(t => t.annotated !== false && t.tagging?.exitModelId === emid)
   *
   * No account selection. No `countsForMetrics`. The WHOLE book, reviewed trades
   * only.
   *
   * ⚠ THE FIRST VERSION OF THIS USED `tradesCountingForMetrics` AND OMITTED THE
   * ANNOTATION FILTER — the exact pair of errors D64 records, on a new tab, in a
   * comment that cited D64 while making them. It was caught by comparing against
   * Josh's screenshot on his real catalogue: DISCRETIONARY matched to the dollar
   * while HARD TARGET read 17/71%/+$126/+$2,148 against beta's
   * 18/72%/+$152/+$2,728. Two opposite errors that cancelled on one model and
   * not the next — self-consistent, plausible, and wrong.
   *
   * ⚠ `!== false`, not `=== true`. Trades written before the flag existed carry
   * null, and beta counts them. `=== true` would silently drop a whole history.
   *
   * ⚠ Beta INCLUDING sim and replay trades here is arguably the wrong product
   * call — an exit model's edge should probably not be measured on replay. But
   * that is Josh's decision to take deliberately, not a port's to make silently
   * (D64's own closing note), so this matches and says so.
   */
  const scoped = useMemo<ExitModelTrade[] | null>(() => {
    if (!rows) return null;
    return rows.filter((t) => t.annotated !== false) as unknown as ExitModelTrade[];
  }, [rows]);

  const board = useMemo<ExitModelBoardRow[] | null>(() => {
    if (!scoped || !exitModels) return null;
    /**
     * 🚨 THE COMMENT THAT USED TO BE HERE WAS THE BUG (#67).
     *
     * It read: "USD: the unit value IS net P&L, as on every other ported
     * analytics surface." That is true only when the unit IS dollars — and the
     * cards below label these figures with `unitCompact(…, unit)`, so with
     * Points on, a model that made $1,234 was reading **"+1,234 pts"**: right
     * label, wrong number, roughly 20× too big on ES and entirely believable.
     *
     * ⚠ A comment stating a false general rule is how this got through review.
     *
     * Beta converts here — `exitModelStats(em.id, undefined, unit)` sums
     * `tradeValue(t, unit)` and skips the trades with no value in that unit,
     * which is exactly what `computeLeaderboardStats` does with a null.
     */
    return exitModelBoard(
      scoped, exitModels,
      (t) => (t.netPnl == null ? null : tradeValue(t as never, unit, contracts ?? [])),
    );
  }, [scoped, exitModels, unit, contracts]);

  const sorted = useMemo(
    () => (board ? sortExitModels(board, sort) : null), [board, sort],
  );

  const untagged = useMemo(() => (scoped ? untaggedCount(scoped) : 0), [scoped]);

  const afterWrite = useCallback(() => { setEditing(undefined); reload(); }, [reload]);

  // ── SETUPS ─────────────────────────────────────────────────────────────────

  /**
   * ── 🚨 THE PLAYBOOK'S POPULATION, TRANSCRIBED FROM `patternStats` ──
   *
   *   source.filter(t => t.core && t.annotated !== false && t.tagging?.patternId === id)
   *
   * No account selection. No `countsForMetrics`. No date range. The whole book,
   * reviewed trades only.
   *
   * ⚠ WRITTEN OUT SEPARATELY FROM `scoped` ABOVE EVEN THOUGH THE TWO RULES ARE
   * IDENTICAL TODAY. They come from two different beta functions, and D77 was
   * written after the same population was got wrong on three surfaces in a row —
   * twice by assuming the obvious shared default. Four duplicated lines are the
   * price of the two tabs being able to diverge without one silently moving the
   * other. Each has its own test naming its own rule.
   *
   * ⚠ `t.core` is not modelled — see `domain/patternBoard.ts`. It is beta's guard
   * against a trade with no core block, and the flattened schema has typed columns
   * where beta has a nested object, so there is no state it could exclude.
   *
   * ⚠ This is NOT Setup Quality's population, and the two genuinely disagree: that
   * leaderboard applies `countsForMetrics` and no annotation filter. A pattern can
   * read a different sample on the two screens. That is beta's behaviour.
   */
  const reviewed = useMemo<PatternBoardTrade[] | null>(() => {
    if (!rows || !mode) return null;
    return rows
      .filter((t) => t.annotated !== false)
      .map((t) => ({
        patternId: t.patternId,
        netPnl: t.netPnl,
        rrRealized: t.rrRealized,
        // ⚠ Needed by the value callback below. Narrowing them away made every
        // pattern read "0 pts" while the dollar view stayed correct — a silent
        // zero that only a points-mode test could see.
        points: t.points,
        contract: t.contract,
        // ⚠ Resolved HERE, with the day-boundary mode, because `domain/` cannot
        // reach one. Under session mode a trade after the 18:00 roll belongs to
        // the NEXT session date, which is what LAST 30D has to count it as.
        effectiveDate: effectiveTradeDate(t, mode),
      }));
  }, [rows, mode]);

  const categories = useMemo(
    () => patternCategories(prefs?.patternCategories), [prefs?.patternCategories],
  );

  /** Beta's cutoff: today minus 30 days, the boundary date itself excluded. */
  const last30After = useMemo(() => (today ? addDays(today, -30) : null), [today]);

  const patBoard = useMemo<PatternBoardRow[] | null>(() => {
    if (!reviewed || !patterns) return null;
    // ── ⚠ IN THE DISPLAY UNIT ──
    //
    // `patternBoard` has taken a value callback since it was written, and its
    // own docstring anticipated this: it notes that a trade with no value in
    // the display unit contributes zero to the 30-day total, and that "in USD
    // every trade has a value, so the two agree on the only unit shipped so
    // far". This is the caller that stops that being the only unit.
    //
    // ⚠ NULL, NOT ZERO, for a trade with no value in the unit — a stop-less
    // trade has no R, and folding a zero into a pattern’s expectancy would drag
    // it toward the middle rather than leaving it out of the average.
    return patternBoard(
      patterns, reviewed,
      (t) => (t.netPnl == null ? null : tradeValue(t as never, unit, contracts ?? [])),
      last30After,
    );
  }, [reviewed, patterns, last30After, unit, contracts]);

  const patSorted = useMemo(
    () => (patBoard ? sortPatterns(patBoard, patSort) : null), [patBoard, patSort],
  );

  const grid = useMemo(
    () => (patSorted ? patternGrid(patSorted, categories) : null), [patSorted, categories],
  );

  const afterPatternWrite = useCallback(() => {
    setEditingPattern(undefined);
    reloadPatterns();
  }, [reloadPatterns]);

  /**
   * "▲ Promote to production" — a candidate PATTERN becomes taggable.
   *
   * ⚠ NOT `promoteVersion`. This touches `status` and no version at all; the two
   * look nearly identical in the UI and are unrelated in the data (D76). And it
   * goes through `savePatternLifecycle` rather than the form's save, so it cannot
   * reconcile over working rules nobody opened.
   */
  const promotePattern = useCallback(async (p: PatternSummary) => {
    if (!window.confirm(
      `Promote "${p.name}" to production? It becomes taggable on live trades and `
      + 'shows in your live analytics.',
    )) return;
    setPatError(null);
    const res = await savePatternLifecycle(
      sb, p.id, p, promoteToProduction(patternLifecycle(p)), userId, null,
    );
    if (res.error) { setPatError(res.error); return; }
    reloadPatterns();
  }, [sb, userId, reloadPatterns]);

  // ── failure and loading ────────────────────────────────────────────────────

  if (emPhase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your exit models</h3>
        <p className="muted">Nothing has been changed — this is a read problem. {emError}</p>
      </div>
    );
  }
  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your trades</h3>
        <p className="muted">
          The catalogue needs them for its per-model stats. Nothing has been changed —
          this is a read problem. {error}
        </p>
      </div>
    );
  }
  if (!sorted || !exitModels || !grid || !patSorted) {
    return <p className="muted">Loading your playbook…</p>;
  }

  const statsFor = (id: string) => sorted.find((r) => r.id === id)?.stats ?? null;
  const patStatsFor = (id: string) => patSorted.find((r) => r.id === id)?.stats ?? null;

  /**
   * ── 🚨 THE FULL RECORD, LOOKED UP — NEVER `row.pattern` CAST UP ──
   *
   * `PatternBoardRow.pattern` is a `BoardPattern`: five fields, all the board
   * needs. At RUNTIME it is the same object as the loaded `PatternSummary`,
   * because that is what was handed in — so `row.pattern as PatternSummary`
   * type-checks and works, right up until someone maps the rows through
   * anything and the extras, versions and criteria are gone.
   *
   * That is D62 exactly: a typed model narrower than the record, laundered by a
   * cast, feeding a form whose save writes what it holds. The editor would open
   * with an empty criteria list and save it over the real one. So the record is
   * resolved from the catalogue by id, and a row that cannot resolve does not
   * open — it never happens, and if it ever does, nothing is written.
   */
  const openPattern = (id: string) => {
    const full = (patterns ?? []).find((p) => p.id === id);
    if (full) setEditingPattern(full);
  };
  const promoteById = (id: string) => {
    const full = (patterns ?? []).find((p) => p.id === id);
    if (full) void promotePattern(full);
  };

  return (
    <div className="patterns-tab">
      <div className="pat-subtabs" role="tablist">
        <button
          type="button" role="tab" aria-selected={sub === 'setups'}
          className={`pat-subtab${sub === 'setups' ? ' active' : ''}`}
          onClick={() => setSub('setups')}
        >
          Setups ({(patterns ?? []).length})
        </button>
        <button
          type="button" role="tab" aria-selected={sub === 'exits'}
          className={`pat-subtab${sub === 'exits' ? ' active' : ''}`}
          onClick={() => setSub('exits')}
        >
          Exit Models ({exitModels.length})
        </button>
      </div>

      {sub === 'setups' ? (
        <>
          <div className="section-header">
            <h3>Pattern Playbook ({(patterns ?? []).length})</h3>
            <span className="grow" />
            <label className="pat-sort">
              <span className="muted">Sort:</span>
              <select
                aria-label="Sort patterns" value={patSort}
                onChange={(e) => setPatSort(e.target.value as PatternSortKey)}
              >
                {PAT_SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            {/* ⚠ Beta has "Manage Categories" beside this. Not built — it edits a
                preferences array, and Settings has no editor for it either. Named
                in the note under the grid rather than shown disabled. */}
            <button type="button" className="btn primary" onClick={() => setEditingPattern(null)}>
              + New Pattern
            </button>
          </div>

          {patError ? (
            <div role="alert" className="import-alert">
              <b>Nothing was changed.</b> {patError}
            </div>
          ) : null}

          {(patterns ?? []).length === 0 ? (
            <div className="card">
              <h3>No patterns yet</h3>
              <p className="muted">
                Define your first setup — e.g. “NY-AM Failed Auction Reversal” — with its
                entry criteria and context filters. Trades you tag with it roll into the
                stats on every card here.
              </p>
            </div>
          ) : (
            <>
              {/* ⚠ A BANNER, NOT A FIFTH COLUMN. A pattern whose stored category
                  name no longer exists is invisible on a screen made of categories,
                  so it gets a chip that opens it — which is the only way to fix it. */}
              {grid.uncategorized.length ? (
                <div className="pat-uncat">
                  ⚠ {grid.uncategorized.length} uncategorized — open and assign a category:
                  {grid.uncategorized.map((r) => (
                    <button
                      key={r.id} type="button" className="tag pat-uncat-chip"
                      onClick={() => openPattern(r.id)}
                    >
                      {r.pattern.name} →
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="pat-cat-grid">
                {grid.columns.map((col) => (
                  <div className="pat-cat-col" key={col.name}>
                    <div className="pat-cat-head" style={{ color: col.color }}>
                      {col.name}<span className="cnt">{col.rows.length}</span>
                    </div>
                    <div className="pat-cat-rule" style={{ background: col.color }} />
                    {col.rows.length ? col.rows.map((r) => (
                      <PatternCard
                        unit={unit}
                        key={r.id} row={r}
                        onOpen={() => openPattern(r.id)}
                        onPromote={() => promoteById(r.id)}
                      />
                    )) : <div className="pat-cat-empty">No patterns yet.</div>}
                  </div>
                ))}
              </div>

              <p className="muted em-untagged">
                Editing the category list itself — beta’s <b>Manage Categories</b> — isn’t
                built here yet. Your {categories.length} categories are read from settings.
              </p>
            </>
          )}
        </>
      ) : (
        <>
          <div className="section-header">
            <h3>Exit Model Catalog ({exitModels.length})</h3>
            <span className="grow" />
            <label className="pat-sort">
              <span className="muted">Sort:</span>
              <select
                aria-label="Sort exit models" value={sort}
                onChange={(e) => setSort(e.target.value as ExitModelSortKey)}
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <button type="button" className="btn primary" onClick={() => setEditing(null)}>
              + New Exit Model
            </button>
          </div>

          {exitModels.length === 0 ? (
            <div className="card">
              <h3>No exit models yet</h3>
              <p className="muted">
                An exit model is how you got OUT — the stop, target and scale-out rules you
                actually followed. The trade form requires one whenever a pattern is set,
                so this is the list that unblocks tagging.
              </p>
            </div>
          ) : (
            <div className="em-list">
              {sorted.map((row) => {
                const model = exitModels.find((m) => m.id === row.id);
                if (!model) return null;
                return <ExitModelCard key={row.id} model={model} row={row} onOpen={setEditing} unit={unit} />;
              })}
            </div>
          )}

          {/* ⚠ Reported, not ranked. An untagged trade is a gap in the review, but
              there is no exit model to open — so it is a note, not a card. */}
          {untagged ? (
            <p className="muted em-untagged">
              {untagged} trade{untagged === 1 ? '' : 's'} in scope carry no exit model, so they
              are in none of these figures.
            </p>
          ) : null}
        </>
      )}

      {editing !== undefined ? (
        <ExitModelEditor
          // ⚠ Keyed, for the reason the trade form is: the form seeds once, so
          // opening a second model without this shows the first one's rules.
          key={editing?.id ?? 'new'}
          model={editing}
          stats={editing ? statsFor(editing.id) : null}
          unit={unit}
          taggedTrades={editing ? (statsFor(editing.id)?.sample ?? 0) : 0}
          newId={newTradeId}
          onSaved={afterWrite}
          onDeleted={afterWrite}
          onCancel={() => setEditing(undefined)}
        />
      ) : null}

      {editingPattern !== undefined ? (
        <PatternEditor
          // ⚠ Keyed for the same reason as the exit-model form: it seeds its state
          // once, so opening a second pattern without this shows the first one's
          // criteria — and the save would write them onto the second record.
          key={editingPattern?.id ?? 'new'}
          pattern={editingPattern}
          stats={editingPattern ? patStatsFor(editingPattern.id) : null}
          unit={unit}
          categories={categories}
          newId={newTradeId}
          now={nowIso}
          onSaved={afterPatternWrite}
          onDeleted={afterPatternWrite}
          onLifecycle={afterPatternWrite}
          onCancel={() => setEditingPattern(undefined)}
        />
      ) : null}
    </div>
  );
}

/**
 * ⚠ The clock lives HERE, not in the editor and certainly not in `domain/`. The
 * lifecycle stamps `createdAt` on a new version, and passing it in keeps every
 * version function testable against a fixed timestamp.
 */
const nowIso = () => new Date().toISOString();

function PatternCard(
  { row, onOpen, onPromote, unit }:
  { row: PatternBoardRow; onOpen: () => void; onPromote: () => void; unit: PnlUnit },
) {
  const s = row.stats;
  const p = row.pattern;
  const candidate = p.status === 'candidate';
  return (
    <div
      className="pattern-card" role="button" tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <div className="pcard-top">
        <div className="pcard-name">{p.name}</div>
        {/* ⚠ The chip says WHY it matters, not just that it exists: a candidate is
            not offered by the trade form's picker at all. */}
        {candidate ? (
          <span
            className="pat-cand-chip"
            title="Candidate — not taggable on live trades until you promote it"
          >🧪 Candidate</span>
        ) : null}
      </div>
      {/* ⚠ Truncated at 90 characters, as beta does. Descriptions run to sentences
          and a card that grows to five lines breaks the column scan. */}
      <div className="muted pcard-desc">{p.description.slice(0, 90)}</div>
      <div className="pcard-stat">
        <span>SAMPLE <b>{s.sample}</b></span>
        <span>WIN <b className={winRateClass(s.winRate, s.totalPnl)}>
          {s.sample ? pct(s.winRate, 0) : '—'}
        </b></span>
        <span>EXP <b className={s.expectancy > 0 ? 'green' : s.expectancy < 0 ? 'red' : ''}>
          {s.sample ? unitCompact(s.expectancy, unit) : '—'}
        </b></span>
      </div>
      {candidate ? (
        <div className="pcard-actions">
          <button
            type="button" className="btn primary"
            // ⚠ Stops the card's own open handler — beta does the same with an
            // explicit `if (ev.target.closest(...)) return`. Without it, promoting
            // also opens the editor behind the confirm.
            onClick={(e) => { e.stopPropagation(); onPromote(); }}
          >
            ▲ Promote to production
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The old client's win-rate colouring, ported literally.
 *
 * ⚠ It is NOT a simple threshold: red requires a losing pattern AS WELL AS a low
 * win rate. A 30%-win setup that is up money is a runner strategy working exactly
 * as designed, and painting it red would call the user's best edge a leak.
 */
function winRateClass(winRate: number, totalPnl: number): string {
  if (winRate >= 55) return 'green';
  if (winRate < 45 && totalPnl < 0) return 'red';
  return '';
}

function ExitModelCard(
  { model, row, onOpen, unit }:
  {
    model: ExitModelSummary; row: ExitModelBoardRow;
    onOpen: (m: ExitModelSummary) => void; unit: PnlUnit;
  },
) {
  const s = row.stats;
  const open = () => onOpen(model);
  return (
    <div
      className="em-card" role="button" tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
    >
      <div className="em-name">{model.name}</div>
      {/* ⚠ All three lines render even when empty — the shape of an exit model is
          stop / target / scale, and a card that drops the blank ones changes
          height per model and hides which rule is missing. */}
      <Rule label="Stop" value={model.stopRule} />
      <Rule label="Target" value={model.targetRule} />
      <Rule label="Scale" value={model.scaleOutRule} />
      <div className="em-stats-row">
        <Figure k="Sample" v={String(s.sample)} />
        {/* ⚠ THE SAME THREE RULES THE PATTERN CARD SIXTY LINES ABOVE USES, and
            they used to disagree. `winRateClass` is not a plain threshold — red
            needs a LOSING model as well as a low win rate, because a 30%-win
            runner strategy that is up money is working as designed. And the
            expectancy test is three-way: `> 0` green, `< 0` red, ZERO NEUTRAL.
            This card had no win colour at all and used `>= 0`, so a model that
            had made exactly nothing read as a winner. */}
        <Figure
          k="Win" v={s.sample ? pct(s.winRate, 0) : '—'}
          cls={winRateClass(s.winRate, s.totalPnl)}
        />
        <Figure
          k="Exp" v={s.sample ? unitCompact(s.expectancy, unit) : '—'}
          cls={s.sample ? threeWay(s.expectancy) : ''}
        />
        <Figure
          k="P&L" v={s.sample ? unitCompact(s.totalPnl, unit) : '—'}
          cls={s.sample ? threeWay(s.totalPnl) : ''}
        />
      </div>
    </div>
  );
}

function Rule({ label, value }: { label: string; value: string }) {
  return (
    <div className="em-rule">
      <b>{label}:</b>{' '}
      {/* ⚠ TRUNCATED AT 90, as beta does and as the pattern card's description
          already does — a rule can run to a paragraph and an untruncated card
          grows to five lines, which breaks the column scan.

          ⚠ Beta ALSO drops a blank rule line entirely. This client renders all
          three on purpose: the shape of an exit model is stop / target / scale,
          and omitting the blanks changes card height per model and hides WHICH
          rule is missing. That divergence is deliberate and is pinned by its own
          test; it needs a PD number, not a code change. */}
      <span className="muted">{value.trim().slice(0, 90) || <em>not set</em>}</span>
    </div>
  );
}

function Figure({ k, v, cls }: { k: string; v: string; cls?: string }) {
  return (
    <span className="em-fig">
      <span className="em-fig-k">{k}</span>
      <b className={cls ?? ''}>{v}</b>
    </span>
  );
}
